"use client";

import { motion } from "framer-motion";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  type ActiveFilter,
  type FilterFieldDef,
  type FilterOperator,
  FILTER_OPERATORS,
  NUMBER_FILTER_OPERATORS,
} from "./types";
import { SegmentSelect } from "./segment-select";
import { FieldIcon } from "./field-icon";
import { SearchableSegment } from "./searchable";
import { DateRangeSegment } from "./date-range";
import { FilterTextValue } from "./text";
import { FilterNumberValue } from "./number";
import { FilterSelectValue } from "./select";
import { Icon } from "@/components/ui/icon";

export function resolveOp(type: FilterFieldDef["type"]): FilterOperator {
  if (type === "text") return "contains";
  if (type === "date_range") return "between";
  if (type === "number") return "gte";
  return "is";
}

export function resolveDefaultVal(field: FilterFieldDef): string {
  if (field.type === "text" || field.type === "date_range" || field.type === "number")
    return "";
  if (field.type === "searchable")
    return field.searchableOptions?.[0]?.id ?? "";
  return field.options[0]?.value ?? "";
}

export function FilterRow({
  filter,
  fields,
  usedFieldKeys,
  onUpdate,
  onRemove,
}: {
  filter: ActiveFilter;
  fields: FilterFieldDef[];
  usedFieldKeys: string[];
  onUpdate: (updated: ActiveFilter) => void;
  onRemove: () => void;
}) {
  const field = fields.find((f) => f.key === filter.field);

  const fieldOptions = fields
    .filter((f) => f.key === filter.field || !usedFieldKeys.includes(f.key))
    .map((f) => ({ value: f.key, label: f.label }));

  const isText = field?.type === "text";
  const isDateRange = field?.type === "date_range";
  const isSearchable = field?.type === "searchable";
  const isNumber = field?.type === "number";
  const isSelect = !isText && !isDateRange && !isSearchable && !isNumber;
  const operatorOptions = isNumber ? NUMBER_FILTER_OPERATORS : FILTER_OPERATORS;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 400, mass: 0.4 }}
      className="overflow-hidden"
    >
      <div className="flex items-center py-0.5 w-full min-w-0">
        <div className="flex h-6 w-full min-w-0 max-w-full items-center overflow-hidden rounded-md border border-border bg-contrast/3">
          <div className="flex h-full shrink-0 items-center">
            <SegmentSelect
              value={filter.field}
              options={fieldOptions}
              onChange={(v) => {
                const nf = fields.find((f) => f.key === v);
                if (!nf) return;
                onUpdate({
                  field: v,
                  operator: resolveOp(nf.type),
                  value: resolveDefaultVal(nf),
                });
              }}
              className="text-contrast/60"
              dropdownContent={(opt) => {
                const f = fields.find((ff) => ff.key === opt.value);
                return (
                  <span className="flex items-center gap-1.5">
                    {f && <FieldIcon name={f.icon} />}
                    {opt.label}
                  </span>
                );
              }}
            >
              <span className="flex items-center gap-1">
                {field && <FieldIcon name={field.icon} />}
                {field?.label ?? "Field"}
              </span>
            </SegmentSelect>
          </div>

          <div className="w-px self-stretch bg-contrast/10 shrink-0" />

          {isText ? (
            <div className="shrink-0 h-full flex items-center px-1.5 text-[13px] font-medium text-contrast/40 whitespace-nowrap">
              contains
            </div>
          ) : isDateRange ? (
            <div className="shrink-0 h-full flex items-center px-1.5 text-[13px] font-medium text-contrast/40 whitespace-nowrap">
              between
            </div>
          ) : (
            <div className="flex h-full shrink-0 items-center">
              <SegmentSelect
                value={filter.operator}
                options={operatorOptions}
                onChange={(v) =>
                  onUpdate({ ...filter, operator: v as FilterOperator })
                }
                className="text-contrast/40"
              >
                {operatorOptions.find((o) => o.value === filter.operator)
                  ?.label ?? "is"}
              </SegmentSelect>
            </div>
          )}

          <div className="w-px self-stretch bg-contrast/10 shrink-0" />

          <div className="min-w-0 flex-1 overflow-hidden flex items-center">
            {isText && field ? (
              <FilterTextValue
                filter={filter}
                field={field}
                onUpdate={onUpdate}
              />
            ) : isNumber && field ? (
              <FilterNumberValue
                filter={filter}
                field={field}
                onUpdate={onUpdate}
              />
            ) : isDateRange ? (
              <DateRangeSegment
                value={filter.value}
                onChange={(v) => onUpdate({ ...filter, value: v })}
              />
            ) : isSearchable && field?.searchableOptions ? (
              <SearchableSegment
                value={filter.value}
                onChange={(v) => onUpdate({ ...filter, value: v })}
                options={field.searchableOptions}
                placeholder={field.placeholder}
                presentation={field.searchablePresentation}
              />
            ) : isSelect && field?.options ? (
              <FilterSelectValue
                filter={filter}
                field={field}
                onUpdate={onUpdate}
              />
            ) : null}
          </div>

          <div className="w-px self-stretch bg-contrast/10 shrink-0" />

          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${field?.label ?? "field"} filter`}
            className={cn(
              buttonVariants({ variant: "ghost", size: "icon-xs" }),
              "h-full w-6 shrink-0 rounded-none text-contrast/40 hover:bg-[rgba(255,80,80,0.08)] hover:text-[#ff6b6b]",
            )}
          >
            <Icon name="IconTrashCanSimple" size={12} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
